import { useState, useEffect, useRef } from "react";

import {
  isAISuggestArray,
  type AISuggest,
  type SuggestionContext,
} from "src/app/components/AISuggests/types";
import { useJwtRefresh } from "src/app/AIChat/hooks/useJwtRefresh";
import { TimeoutId } from "src/types";

const fetchAISuggestions = async (
  context: SuggestionContext,
  token: string,
  signal: AbortSignal,
): Promise<AISuggest[]> => {
  const url = "https://n8n-api.idh.cloud/webhook/ai-suggestions-generator";

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      context,
      currentUrl: window.location.href,
    }),
    signal,
  });

  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const result: unknown = await response.json();

  return isAISuggestArray(result) ? result : [];
};

const useGetAISuggests = (context: SuggestionContext | null) => {
  const [data, setData] = useState<AISuggest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const abortController = useRef<AbortController | null>(null);
  const isFirstRender = useRef<boolean>(true);
  const { getValidToken } = useJwtRefresh();
  const getValidTokenRef = useRef(getValidToken);
  getValidTokenRef.current = getValidToken;

  useEffect(() => {
    if (!context) {
      setData([]);

      return;
    }

    const fetchSuggests = async () => {
      const token = await getValidTokenRef.current();
      if (!token) return;

      abortController.current?.abort();
      abortController.current = new AbortController();

      setIsLoading(true);
      try {
        const suggests = await fetchAISuggestions(
          context,
          token,
          abortController.current.signal,
        );
        setData(suggests);
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          console.error("useGetAISuggests:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    let timeoutId: TimeoutId | undefined;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchSuggests();
    } else {
      timeoutId = setTimeout(fetchSuggests, 5000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      abortController.current?.abort();
    };
  }, [context]);

  return { data, isLoading };
};

export default useGetAISuggests;
